// AGC: Translation Context Provider
// AGC: Manages language state and syncs with Firestore
// AGC: Provides caching for offline support

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation as useI18nextTranslation } from 'react-i18next'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../services/firebase-auth'
import i18n, { 
  loadTranslations, 
  changeLanguage as i18nChangeLanguage,
  getCurrentLanguage,
  getAvailableLanguages,
  LANGUAGES,
  type LanguageCode 
} from '../i18n'

// AGC: Import local translation files as fallback
import enLocal from '../i18n/locales/en.json'
import ptLocal from '../i18n/locales/pt.json'
import esLocal from '../i18n/locales/es.json'

// AGC: Local translation files map
const LOCAL_TRANSLATIONS: Record<LanguageCode, object> = {
  en: enLocal,
  pt: ptLocal,
  es: esLocal,
}

// AGC: localStorage key for cached translations
const CACHE_KEY = 'droidfarm_translations_cache'
const CACHE_TIMESTAMP_KEY = 'droidfarm_translations_timestamp'
const CACHE_DURATION = 1000 * 60 * 60 // AGC: 1 hour cache

// AGC: Context type definition
interface TranslationContextType {
  // AGC: Current language
  language: LanguageCode
  // AGC: Available languages
  languages: typeof LANGUAGES
  // AGC: Loading state
  isLoading: boolean
  // AGC: Error state
  error: string | null
  // AGC: Change language
  setLanguage: (lang: LanguageCode) => Promise<void>
  // AGC: Refresh translations from server
  refreshTranslations: () => Promise<void>
  // AGC: Get available languages list
  getLanguages: () => { code: string; name: string; flag: string }[]
  // AGC: Translation function (from i18next)
  t: (key: string, options?: object) => string
}

// AGC: Create context with default values
const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

/**
 * AGC: TranslationProvider - Wraps app with translation context
 * AGC: Features:
 * AGC: - Fetches translations directly from Firestore
 * AGC: - Falls back to local JSON files if Firestore is empty
 * AGC: - Caches translations in localStorage for offline support
 * AGC: - Auto-detects user's preferred language
 */
export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useI18nextTranslation()
  const [language, setLanguageState] = useState<LanguageCode>(getCurrentLanguage())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * AGC: Fetch translations directly from Firestore
   * AGC: Falls back to local JSON files if Firestore is empty or unavailable
   */
  const fetchTranslations = useCallback(async (lang: LanguageCode) => {
    try {
      // AGC: Try to fetch from Firestore first
      if (db) {
        const docRef = doc(db, 'translations', lang)
        const docSnap = await getDoc(docRef)
        
        if (docSnap.exists()) {
          const data = docSnap.data()
          console.log(`✅ AGC: Loaded translations from Firestore for ${lang}`)
          return data
        } else {
          console.log(`⚠️ AGC: No translations in Firestore for ${lang}, using local files`)
        }
      } else {
        console.log(`⚠️ AGC: Firestore not available, using local files for ${lang}`)
      }
      
      // AGC: Fallback to local JSON files
      const localTranslations = LOCAL_TRANSLATIONS[lang]
      if (localTranslations) {
        console.log(`✅ AGC: Using local translations for ${lang}`)
        return localTranslations
      }
      
      return null
    } catch (err) {
      console.warn(`⚠️ AGC: Could not fetch translations for ${lang}, using local fallback:`, err)
      
      // AGC: Fallback to local files on error
      const localTranslations = LOCAL_TRANSLATIONS[lang]
      if (localTranslations) {
        console.log(`✅ AGC: Using local translations as fallback for ${lang}`)
        return localTranslations
      }
      
      return null
    }
  }, [])

  /**
   * AGC: Load cached translations from localStorage
   */
  const loadCachedTranslations = useCallback((lang: LanguageCode) => {
    try {
      const cached = localStorage.getItem(`${CACHE_KEY}_${lang}`)
      const timestamp = localStorage.getItem(`${CACHE_TIMESTAMP_KEY}_${lang}`)
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10)
        
        // AGC: Check if cache is still valid
        if (age < CACHE_DURATION) {
          return JSON.parse(cached)
        }
      }
      
      return null
    } catch {
      return null
    }
  }, [])

  /**
   * AGC: Save translations to localStorage cache
   */
  const cacheTranslations = useCallback((lang: LanguageCode, translations: object) => {
    try {
      localStorage.setItem(`${CACHE_KEY}_${lang}`, JSON.stringify(translations))
      localStorage.setItem(`${CACHE_TIMESTAMP_KEY}_${lang}`, Date.now().toString())
    } catch {
      console.warn('⚠️ AGC: Could not cache translations')
    }
  }, [])

  /**
   * AGC: Load translations for a language
   */
  const loadLanguageTranslations = useCallback(async (lang: LanguageCode) => {
    setIsLoading(true)
    setError(null)

    try {
      // AGC: Try to load from cache first
      const cached = loadCachedTranslations(lang)
      
      if (cached) {
        // AGC: Flatten nested object for i18next
        const flatTranslations = flattenObject(cached)
        loadTranslations(lang, flatTranslations)
        console.log(`✅ AGC: Loaded cached translations for ${lang}`)
      }

      // AGC: Fetch fresh translations from server in background
      const serverTranslations = await fetchTranslations(lang)
      
      if (serverTranslations) {
        // AGC: Flatten and load into i18next
        const flatTranslations = flattenObject(serverTranslations)
        loadTranslations(lang, flatTranslations)
        
        // AGC: Cache for offline use
        cacheTranslations(lang, serverTranslations)
        console.log(`✅ AGC: Synced translations from server for ${lang}`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError(message)
      console.error('❌ AGC: Error loading translations:', message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchTranslations, loadCachedTranslations, cacheTranslations])

  /**
   * AGC: Change current language
   */
  const setLanguage = useCallback(async (lang: LanguageCode) => {
    if (lang === language) return

    await i18nChangeLanguage(lang)
    setLanguageState(lang)
    await loadLanguageTranslations(lang)
  }, [language, loadLanguageTranslations])

  /**
   * AGC: Refresh translations from server
   */
  const refreshTranslations = useCallback(async () => {
    // AGC: Clear cache and reload
    localStorage.removeItem(`${CACHE_KEY}_${language}`)
    localStorage.removeItem(`${CACHE_TIMESTAMP_KEY}_${language}`)
    await loadLanguageTranslations(language)
  }, [language, loadLanguageTranslations])

  /**
   * AGC: Get available languages
   */
  const getLanguages = useCallback(() => {
    return getAvailableLanguages()
  }, [])

  // AGC: Load translations on mount and language change
  useEffect(() => {
    loadLanguageTranslations(language)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // AGC: Listen for language changes from i18next
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      const langCode = lng.split('-')[0] as LanguageCode
      if (langCode !== language && langCode in LANGUAGES) {
        setLanguageState(langCode)
      }
    }

    i18n.on('languageChanged', handleLanguageChanged)
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [language])

  const contextValue: TranslationContextType = {
    language,
    languages: LANGUAGES,
    isLoading,
    error,
    setLanguage,
    refreshTranslations,
    getLanguages,
    t,
  }

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  )
}

/**
 * AGC: Hook to use translation context
 */
export const useTranslationContext = (): TranslationContextType => {
  const context = useContext(TranslationContext)
  
  if (context === undefined) {
    throw new Error('useTranslationContext must be used within a TranslationProvider')
  }
  
  return context
}

/**
 * AGC: Flatten nested object to dot notation
 * AGC: Example: { common: { save: 'Save' } } => { 'common.save': 'Save' }
 */
function flattenObject(obj: object, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {}
  
  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key
    
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value, newKey))
    } else if (typeof value === 'string') {
      result[newKey] = value
    }
  }
  
  return result
}

export default TranslationContext
